import { useCallback, useEffect, useState } from "react";
import { equipmentApi } from "../../services/equipmentApi";
import { useToast } from "../../context/ToastContext";
import { Card } from "../../components/common/Card";
import { Button } from "../../components/common/Button";
import { Modal } from "../../components/common/Modal";
import { Field } from "../../components/common/Field";
import { DataTable } from "../../components/common/DataTable";

/**
 * Equipment on this hive (#250).
 *
 * "No equipment assigned" has been on this tab since V009 with no way to
 * assign any -- app.EquipmentAssignment had a GET and nothing that could
 * write a row, so the empty state described a feature that did not exist.
 *
 * What is AVAILABLE is derived, never stored: inventory quantity is how
 * many you own, and open assignments are how many are out. Subtracting the
 * two here means the picker cannot drift from the server's own ceiling.
 */
export function EquipmentTab({ hive }) {
  const { notify } = useToast();
  const [assignments, setAssignments] = useState(null);
  // How many of them go on this hive (#287). Frames are bought in tens
  // and go out in twos.
  const [howMany, setHowMany] = useState("1");
  const [inventory, setInventory] = useState([]);
  // Three states, not one: nothing owned, nothing free, or we could not
  // find out. The empty state used to claim the second in all three cases.
  const [inventoryFailed, setInventoryFailed] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [choice, setChoice] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    // allSettled: the assignment list is the point of the tab and must
    // render even if the inventory call fails -- that only costs the picker.
    Promise.allSettled([equipmentApi.assignments(), equipmentApi.list()]).then(([a, inv]) => {
      setAssignments(a.status === "fulfilled" ? a.value.assignments : []);
      // `equipment`, not `inventory` -- GET /equipment/inventory answers
      // with {"equipment": [...]}. Reading the wrong key meant the picker
      // was ALWAYS empty, and the empty state then asserted the one wrong
      // explanation of three: that everything was already out on a hive.
      setInventory(inv.status === "fulfilled" ? inv.value.equipment || [] : []);
      setInventoryFailed(inv.status === "rejected");
    });
  }, []);
  useEffect(() => { load(); }, [load]);

  const mine = (assignments || []).filter((a) => a.hive_id === hive.hive_id);
  // SUM the quantities, do not count the rows (V073). One row can now be
  // "2 frames on this hive", so counting rows would report eight left out
  // of ten when six are actually out.
  //
  // ?? 1 rather than || 1: a row written before V073 has no quantity at
  // all, and it meant one.
  const outByEquipment = (assignments || [])
    .filter((a) => !a.return_date)
    .reduce((acc, a) => {
      acc[a.equipment_id] = (acc[a.equipment_id] || 0) + Number(a.quantity ?? 1);
      return acc;
    }, {});
  const available = inventory
    .map((item) => ({ ...item, left: Number(item.quantity || 0) - (outByEquipment[item.equipment_id] || 0) }))
    .filter((item) => item.left > 0);

  // The assignment being returned, and the answers about it.
  const [returnRow, setReturnRow] = useState(null);
  const [returnForm, setReturnForm] = useState({ quantity: "", condition: "Reusable", note: "" });

  async function assign() {
    if (!choice) {
      notify("Pick something to put on this hive", "warning");
      return;
    }
    const wanted = Number(howMany) || 1;
    const picked = available.find((i) => String(i.equipment_id) === String(choice));
    if (picked && wanted > picked.left) {
      // Said here as well as by the server, because the number is on the
      // screen in front of them -- being refused by a round trip for
      // something the page already knew is a poor way to find out.
      notify(`You only have ${picked.left} ${picked.item_type} left`, "warning");
      return;
    }
    setBusy(true);
    try {
      await equipmentApi.assign({
        equipment_id: Number(choice),
        hive_id: hive.hive_id,
        quantity: wanted,
      });
      notify(wanted === 1 ? "Equipment assigned" : `${wanted} assigned`, "success");
      setAddOpen(false);
      setChoice("");
      setHowMany("1");
      load();
    } catch (err) {
      // The server names both numbers when everything is already out.
      notify(err.apiMessage || "Could not assign that", "danger");
    } finally {
      setBusy(false);
    }
  }

  /*
   * Bringing kit back (#287).
   *
   * Was a window.prompt for free text, which could only ever return the
   * WHOLE assignment -- ten frames went out, ten came back, and a model
   * with no partial path quietly turns every assignment into a disposal.
   *
   * Two questions now, because they are the two that change what the
   * numbers mean: how many, and what state. Both are pre-filled with the
   * common answer (all of them, reusable) so the ordinary case is still
   * open-and-confirm.
   */
  function bringBack(row) {
    setReturnRow(row);
    setReturnForm({ quantity: String(row.quantity ?? 1), condition: "Reusable", note: "" });
  }

  async function submitReturn() {
    const row = returnRow;
    const quantity = Number(returnForm.quantity);
    if (!quantity || quantity < 1) {
      notify("How many are coming back?", "warning");
      return;
    }
    if (quantity > (row.quantity ?? 1)) {
      // Checked here as well as on the server, so the answer arrives
      // before the round trip rather than after it.
      notify(`Only ${row.quantity} on that hive.`, "warning");
      return;
    }
    setBusy(true);
    try {
      const r = await equipmentApi.returnItem(row.assignment_id, {
        quantity,
        condition: returnForm.condition,
        condition_on_return: returnForm.note.trim() || undefined,
      });
      // Says what it did to the counts, because "returned" alone does not
      // distinguish the two conditions -- and they mean different things
      // for what you can put back on a hive tomorrow.
      notify(
        returnForm.condition === "Broken"
          ? `${quantity} marked broken. Still yours, not available until repaired.`
          : `${quantity} back on the shelf.`,
        "success",
      );
      if (r.still_out) notify(`${r.still_out} still on this hive.`, "info");
      setReturnRow(null);
      load();
    } catch (err) {
      notify(err.apiMessage || "Could not record that return", "danger");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card
      title="Equipment on this hive"
      actions={<Button variant="secondary" size="sm" onClick={() => setAddOpen(true)}>Assign equipment</Button>}
    >
      <DataTable
        rows={mine}
        loading={assignments === null}
        rowKey="assignment_id"
        emptyTitle="No equipment assigned"
        emptyDescription="Boxes, feeders and frames you put on this hive show up here."
        columns={[
          { key: "item_type", header: "Item" },
          // HOW MANY (reported 25 Aug: the list said "Frame" with no
          // count). V073 made an assignment carry a quantity precisely so
          // ten frames stopped being ten rows -- and then this table went
          // on showing one row that looked like one frame.
          //
          // ?? 1 rather than || 1: a row written before V073 has no
          // quantity and meant exactly one.
          { key: "quantity", header: "How many", render: (r) => r.quantity ?? 1 },
          { key: "assignment_date", header: "Assigned", render: (r) => new Date(r.assignment_date).toLocaleDateString() },
          { key: "return", header: "", render: (r) => (
            <Button size="sm" variant="secondary" onClick={() => bringBack(r)}>Bring back</Button>
          ) },
        ]}
      />

      <Modal
        open={returnRow !== null}
        onClose={() => setReturnRow(null)}
        title={returnRow ? `Bring back ${returnRow.item_type}` : "Bring it back"}
        footer={<Button onClick={submitReturn} disabled={busy}>{busy ? "Saving…" : "Bring back"}</Button>}
      >
        {returnRow && (
          <div style={{ display: "grid", gap: "var(--space-3)" }}>
            <Field
              label="How many?"
              hint={`${returnRow.quantity} on this hive. Leave as is to bring all of them back.`}
            >
              {(id, aria) => (
                <input id={id} {...aria} type="number" min="1" max={returnRow.quantity}
                  className="touch-target" value={returnForm.quantity}
                  onChange={(e) => setReturnForm((f) => ({ ...f, quantity: e.target.value }))} />
              )}
            </Field>

            {/* No "New". New is a state kit is BOUGHT in, not one it comes
                off a hive in, and offering it would invite recording a
                season-old frame as new. */}
            <Field label="What state is it in?">
              {(id, aria) => (
                <select id={id} {...aria} className="touch-target" value={returnForm.condition}
                  onChange={(e) => setReturnForm((f) => ({ ...f, condition: e.target.value }))}>
                  <option value="Reusable">Fine — goes back on the shelf</option>
                  <option value="Broken">Broken — needs repair before it goes out again</option>
                </select>
              )}
            </Field>

            {returnForm.condition === "Broken" && (
              // Said before they save, because the surprising part is
              // that a broken frame is still counted as YOURS.
              <p style={{ margin: 0, fontSize: "var(--font-size-sm)", color: "var(--color-text-muted)" }}>
                It stays in your inventory — you still own it. It just won&apos;t
                count as available until you record it repaired.
              </p>
            )}

            <Field label="Anything worth noting? (optional)" hint="Stays in the hive's history.">
              {(id, aria) => (
                <input id={id} {...aria} className="touch-target" value={returnForm.note}
                  onChange={(e) => setReturnForm((f) => ({ ...f, note: e.target.value }))}
                  placeholder="Wax moth damage on two" />
              )}
            </Field>
          </div>
        )}
      </Modal>

      <Modal open={addOpen} onClose={() => setAddOpen(false)} title="Assign equipment"
        footer={<Button onClick={assign} disabled={busy || available.length === 0}>{busy ? "Assigning…" : "Assign"}</Button>}>
        {available.length === 0 ? (
          <p style={{ margin: 0 }}>
            {inventoryFailed
              ? "Couldn't load your equipment just now. Check your connection and try again."
              : inventory.length === 0
                ? "You haven't added any equipment yet. Add boxes, frames or feeders under Equipment first."
                : "Everything in your inventory is already out on a hive. Bring something back, or add more under Equipment."}
          </p>
        ) : (
          <Field label="What are you putting on this hive?">
            {(id) => (
              <select id={id} className="touch-target" value={choice} onChange={(e) => setChoice(e.target.value)}>
                <option value="">Choose an item…</option>
                {available.map((item) => (
                  <option key={item.equipment_id} value={item.equipment_id}>
                    {item.item_type} — {item.left} available
                  </option>
                ))}
              </select>
            )}
          </Field>
        )}

        {/* How many (#287). A box of ten frames goes out in twos as hives
            expand or comb gets damaged, so a quantity that could not be
            split was not really a quantity. Shown only once something is
            picked -- "how many" of nothing is not a question. */}
        {available.length > 0 && choice && (
          <Field label="How many?">
            {(id) => (
              <input
                id={id}
                type="number"
                min="1"
                max={available.find((i) => String(i.equipment_id) === String(choice))?.left ?? 1}
                className="touch-target"
                value={howMany}
                onChange={(e) => setHowMany(e.target.value)}
              />
            )}
          </Field>
        )}
      </Modal>
    </Card>
  );
}
